import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
    searchUsers, 
    getRankings, 
    getActivities, 
    getGlobalStats,
    getPaginatedUsers,
    getSafeUsers
} from '../../services/supabaseService';

export const Social: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'highlights' | 'members'>('highlights');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [rankLoading, setRankLoading] = useState(false);
  
  const [activities, setActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesLimit, setActivitiesLimit] = useState(6);
  const [loadingMoreActivities, setLoadingMoreActivities] = useState(false);

  const [globalStats, setGlobalStats] = useState<any>(null);
  const [collectionRankings, setCollectionRankings] = useState<any[]>([]);
  const [offersRankings, setOffersRankings] = useState<any[]>([]);

  // Members List State
  const [members, setMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersPage, setMembersPage] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const membersPerPage = 20;

  const fetchActivitiesData = async (limit: number) => {
    try {
      const data = await getActivities(limit);
      setActivities(data || []);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setActivitiesLoading(false);
      setLoadingMoreActivities(false);
    }
  };

  useEffect(() => {
    setActivitiesLoading(true);
    fetchActivitiesData(activitiesLimit);
  }, []);

  const handleLoadMoreActivities = async () => {
    setLoadingMoreActivities(true);
    const newLimit = activitiesLimit + 6;
    setActivitiesLimit(newLimit);
    await fetchActivitiesData(newLimit);
  };

  useEffect(() => {
    if (activeTab === 'members') {
      const fetchMembers = async () => {
        setMembersLoading(true);
        try {
          const { users, total } = await getPaginatedUsers(membersPage, membersPerPage);
          setMembers(users || []);
          setTotalMembers(total || 0);
        } catch (err) {
          console.error('Error fetching members:', err);
        } finally {
          setMembersLoading(false);
        }
      };
      fetchMembers();
    }
  }, [activeTab, membersPage]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.trim().length >= 2) {
        setIsSearching(true);
        try {
          const results = await searchUsers(searchTerm);
          setSearchResults(results || []);
        } catch (err) {
          console.error('Error searching users:', err);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    const fetchAllRankings = async () => {
      setRankLoading(true);
      try {
        const [rankingData, stats, safeUsersList] = await Promise.all([
          getRankings(5),
          getGlobalStats(),
          getSafeUsers()
        ]);
        setCollectionRankings(rankingData?.topCollectors || []);
        setOffersRankings(rankingData?.topTraders || []);

        const usersCount = (safeUsersList && Array.isArray(safeUsersList) && safeUsersList.length > 0)
          ? safeUsersList.length
          : (stats?.users || 0);

        setGlobalStats({
          ...stats,
          users: usersCount
        });
      } catch (err) {
        console.error('Error loading rankings/stats:', err);
      } finally {
        setRankLoading(false);
      }
    };
    fetchAllRankings();
  }, []);

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-in fade-in duration-500 px-2 sm:px-4 pb-16">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 sm:gap-6 border-b border-slate-800/80 pb-4 sm:pb-6">
        <div className="space-y-1 sm:space-y-2">
          <motion.h1 
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-2xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight uppercase italic leading-none"
          >
            Comunidade
          </motion.h1>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="flex items-center space-x-2.5"
          >
            <div className="h-px w-6 sm:w-8 bg-purple-500"></div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">Onde os tubarões se encontram</p>
          </motion.div>
        </div>

        {/* Tab Switcher - Balanced 50/50 on mobile */}
        <div className="grid grid-cols-2 sm:flex p-1 bg-slate-900/80 border border-slate-800 rounded-xl sm:rounded-2xl w-full sm:w-auto shrink-0">
          <button 
            type="button"
            onClick={() => setActiveTab('highlights')}
            className={`py-2 sm:py-2.5 px-4 sm:px-6 rounded-lg sm:rounded-xl text-xs font-bold uppercase tracking-wider transition-colors text-center cursor-pointer ${
              activeTab === 'highlights' 
                ? 'bg-purple-600 text-white' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            Destaques
          </button>
          <button 
            type="button"
            onClick={() => setActiveTab('members')}
            className={`py-2 sm:py-2.5 px-4 sm:px-6 rounded-lg sm:rounded-xl text-xs font-bold uppercase tracking-wider transition-colors text-center cursor-pointer ${
              activeTab === 'members' 
                ? 'bg-purple-600 text-white' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            Membros
          </button>
        </div>
      </div>

      {/* Global Community Search Bar - Prominently accessible on all screen sizes */}
      <div className="relative w-full">
        <div className="relative group">
          <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-purple-400 transition-colors text-xs pointer-events-none"></i>
          <input 
            type="text" 
            placeholder="Buscar colecionador ou jogador por @nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-900/50 border border-slate-800 rounded-xl py-2.5 sm:py-3 pl-10 pr-9 text-xs font-medium text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs cursor-pointer"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {/* Live Search Autocomplete Dropdown */}
        <AnimatePresence>
          {searchTerm.trim().length >= 2 && (
            <motion.div 
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="absolute left-0 right-0 top-full mt-2 bg-slate-900 border border-slate-800 rounded-xl p-2 shadow-2xl z-30 space-y-1 max-h-72 overflow-y-auto"
            >
              {isSearching ? (
                <div className="p-3 text-center text-slate-400 text-xs font-semibold flex items-center justify-center gap-2">
                  <i className="fas fa-circle-notch animate-spin text-purple-400 text-xs"></i>
                  <span>Pesquisando no cardume...</span>
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map(user => (
                  <Link 
                    key={user.id} 
                    to={`/perfil/${user.username}`} 
                    onClick={() => setSearchTerm('')}
                    className="flex items-center space-x-3 p-2.5 rounded-lg hover:bg-slate-800/80 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden border border-slate-800 group-hover:border-purple-500 transition-colors shrink-0">
                      <img 
                        src={user.avatar || `https://ui-avatars.com/api/?name=${user.username}&background=0D0D12&color=fff`} 
                        className="w-full h-full object-cover" 
                        alt="" 
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white group-hover:text-purple-300 transition-colors truncate">
                        @{user.username}
                      </p>
                      {user.fullname && (
                        <p className="text-[10px] text-slate-400 truncate">
                          {user.fullname}
                        </p>
                      )}
                    </div>
                    <i className="fas fa-chevron-right text-[10px] text-slate-600 group-hover:text-purple-400 transition-colors"></i>
                  </Link>
                ))
              ) : (
                <div className="p-3 text-center text-slate-500 text-xs font-medium">
                  Nenhum jogador encontrado com "{searchTerm}".
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main Content Area based on Active Tab */}
      {activeTab === 'highlights' ? (
        <div className="space-y-6 sm:space-y-8">
          {/* Stats Bento Grid Header - 2x2 on mobile, 4 columns on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4">
            {[
              { label: 'Jogadores', value: globalStats?.users ?? '...', icon: 'fa-users', color: 'purple', textClass: 'text-purple-400', bgClass: 'bg-purple-600/10 border-purple-500/20' },
              { label: 'Cartas', value: globalStats?.products ?? '...', icon: 'fa-layer-group', color: 'pink', textClass: 'text-pink-400', bgClass: 'bg-pink-600/10 border-pink-500/20' },
              { label: 'Arenas', value: globalStats?.stores ?? '...', icon: 'fa-store', color: 'emerald', textClass: 'text-emerald-400', bgClass: 'bg-emerald-600/10 border-emerald-500/20' },
              { label: 'Torneios', value: globalStats?.tournaments ?? '...', icon: 'fa-trophy', color: 'amber', textClass: 'text-amber-400', bgClass: 'bg-amber-600/10 border-amber-500/20' },
            ].map((stat, i) => (
              <div 
                key={i}
                className="bg-slate-900/50 border border-slate-800 p-3.5 sm:p-5 rounded-2xl transition-colors hover:border-slate-700"
              >
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center border ${stat.bgClass} ${stat.textClass} mb-2 sm:mb-3`}>
                  <i className={`fas ${stat.icon} text-xs`}></i>
                </div>
                <p className="text-lg sm:text-2xl font-black text-white">{stat.value}</p>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Grid Layout: Hall da Fama (Rankings) & Feed Global */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
            {/* Left / Top: Hall da Fama */}
            <div className="lg:col-span-7 xl:col-span-7 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-black text-white tracking-tight uppercase italic flex items-center gap-2">
                  <i className="fas fa-trophy text-yellow-500 text-sm"></i>
                  <span>Hall da Fama</span>
                </h2>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Top 5</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Ranking de Coleção */}
                <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 sm:p-5 flex flex-col justify-between space-y-4">
                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 bg-purple-600/20 rounded-lg flex items-center justify-center text-purple-400 border border-purple-500/30 shrink-0">
                      <i className="fas fa-crown text-xs"></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-white truncate">Top Colecionadores</h3>
                      <p className="text-[9px] text-slate-400 font-semibold uppercase">Cards Únicos</p>
                    </div>
                  </div>
                  
                  <div className="space-y-1.5 flex-1">
                    {rankLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-10 bg-slate-800/40 rounded-xl animate-pulse"></div>
                      ))
                    ) : collectionRankings.length === 0 ? (
                      <p className="text-xs text-slate-500 py-4 text-center">Nenhum registro ainda.</p>
                    ) : collectionRankings.map((item, idx) => (
                      <Link 
                        key={item.user_id || `coll-${idx}`} 
                        to={`/perfil/${item.username}`} 
                        className="flex items-center justify-between p-2 sm:p-2.5 rounded-xl bg-slate-950/40 border border-slate-800/60 hover:border-purple-500/40 hover:bg-slate-800/40 transition-colors group/item"
                      >
                        <div className="flex items-center space-x-2.5 min-w-0 mr-2 flex-1">
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-black shrink-0 ${
                            idx === 0 ? 'bg-yellow-500 text-black' : idx === 1 ? 'bg-slate-300 text-black' : idx === 2 ? 'bg-amber-600 text-white' : 'bg-slate-800 text-slate-400'
                          }`}>
                            {idx + 1}
                          </div>
                          <span className="text-xs font-bold text-slate-300 group-hover/item:text-purple-300 transition-colors truncate">
                            @{item.username}
                          </span>
                        </div>
                        <span className="text-xs font-black text-emerald-400 shrink-0 font-mono">
                          {item.total_cards || 0}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Ranking de Ofertas */}
                <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 sm:p-5 flex flex-col justify-between space-y-4">
                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 bg-pink-600/20 rounded-lg flex items-center justify-center text-pink-400 border border-pink-500/30 shrink-0">
                      <i className="fas fa-right-left text-xs"></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-white truncate">Top Negociantes</h3>
                      <p className="text-[9px] text-slate-400 font-semibold uppercase">Ofertas Ativas</p>
                    </div>
                  </div>
                  
                  <div className="space-y-1.5 flex-1">
                    {rankLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-10 bg-slate-800/40 rounded-xl animate-pulse"></div>
                      ))
                    ) : offersRankings.length === 0 ? (
                      <p className="text-xs text-slate-500 py-4 text-center">Nenhum registro ainda.</p>
                    ) : offersRankings.map((item, idx) => (
                      <Link 
                        key={item.user_id || `off-${idx}`} 
                        to={`/perfil/${item.username}`} 
                        className="flex items-center justify-between p-2 sm:p-2.5 rounded-xl bg-slate-950/40 border border-slate-800/60 hover:border-pink-500/40 hover:bg-slate-800/40 transition-colors group/item"
                      >
                        <div className="flex items-center space-x-2.5 min-w-0 mr-2 flex-1">
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-black shrink-0 ${
                            idx === 0 ? 'bg-yellow-500 text-black' : idx === 1 ? 'bg-slate-300 text-black' : idx === 2 ? 'bg-amber-600 text-white' : 'bg-slate-800 text-slate-400'
                          }`}>
                            {idx + 1}
                          </div>
                          <span className="text-xs font-bold text-slate-300 group-hover/item:text-pink-300 transition-colors truncate">
                            @{item.username}
                          </span>
                        </div>
                        <span className="text-xs font-black text-pink-400 shrink-0 font-mono">
                          {item.total_cards || 0}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right / Bottom: Global Activity Feed */}
            <div className="lg:col-span-5 xl:col-span-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-black text-white tracking-tight uppercase italic flex items-center gap-2">
                  <i className="fas fa-bolt text-purple-400 text-sm"></i>
                  <span>Feed Global</span>
                </h2>
              </div>

              {/* Activity List - Fluid rendering without trapped nested scrollbar on mobile */}
              <div className="space-y-2.5">
                {activitiesLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="h-16 bg-slate-900/40 rounded-xl border border-slate-800/60 animate-pulse"></div>
                    ))}
                  </div>
                ) : activities.length > 0 ? (
                  activities.map((action, idx) => {
                    const username = action.username || action.user_username || action.user?.username || (typeof action.user === 'string' ? action.user : '') || action.user_id_safe;
                    const profileUrl = username ? `/perfil/${username}` : '/perfil';
                    const displayName = action.username || action.user_username || action.user?.username || (typeof action.user === 'string' ? action.user : '') || action.user_display_name || 'usuário';

                    return (
                      <div
                        key={action.id || `act-${idx}`}
                        className="bg-slate-900/50 border border-slate-800/80 p-3 sm:p-3.5 rounded-xl hover:border-slate-700 transition-colors"
                      >
                        <div className="flex items-start gap-2.5">
                          <Link to={profileUrl} className="shrink-0 mt-0.5">
                            <img 
                              src={action.user_avatar || action.avatar || `https://ui-avatars.com/api/?name=${displayName}`} 
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-cover border border-slate-800" 
                              alt="" 
                            />
                          </Link>
                          <div className="flex-1 min-w-0 space-y-0.5">
                            <div className="flex items-center justify-between gap-1.5">
                              <Link to={profileUrl} className="truncate">
                                <span className="text-xs font-bold text-white hover:text-purple-300 transition-colors truncate">
                                  @{displayName}
                                </span>
                              </Link>
                              <span className="text-[9px] text-slate-500 font-medium shrink-0">
                                {action.timestamp}
                              </span>
                            </div>
                            <p className="text-xs text-slate-400 leading-snug line-clamp-2">
                              {action.details?.message || (
                                <>{action.action} <span className="font-bold text-purple-400">{action.target || action.entity}</span></>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 bg-slate-900/20 border border-dashed border-slate-800 rounded-2xl">
                    <p className="text-slate-500 text-xs font-medium">Nenhuma atividade recente encontrada.</p>
                  </div>
                )}
                
                {!activitiesLoading && activities.length > 0 && (
                  <button 
                    type="button"
                    onClick={handleLoadMoreActivities}
                    disabled={loadingMoreActivities}
                    className="w-full py-2.5 bg-slate-900/80 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 rounded-xl text-xs font-bold text-slate-300 hover:text-white uppercase tracking-wider transition-colors flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {loadingMoreActivities && <i className="fas fa-circle-notch animate-spin text-purple-400 text-xs"></i>}
                    <span>{loadingMoreActivities ? 'Carregando...' : 'Carregar Mais Atividades'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Members Tab View */
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-black text-white tracking-tight uppercase italic">
              Membros do Cardume
            </h2>
            <span className="text-xs font-bold text-slate-400">
              {totalMembers > 0 ? `${totalMembers} membros` : ''}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {membersLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-24 bg-slate-900/40 border border-slate-800 rounded-2xl animate-pulse"></div>
              ))
            ) : members.length === 0 ? (
              <div className="col-span-full py-12 text-center text-slate-500 text-xs">
                Nenhum membro encontrado.
              </div>
            ) : members.map(user => (
              <Link 
                key={user.id} 
                to={`/perfil/${user.username}`} 
                className="bg-slate-900/50 border border-slate-800 rounded-2xl p-3 sm:p-4 hover:border-purple-500/50 hover:bg-slate-900/80 transition-colors group flex items-center gap-3.5 min-w-0"
              >
                <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-800 group-hover:border-purple-500 transition-colors shrink-0">
                  <img 
                    src={user.avatar || `https://ui-avatars.com/api/?name=${user.username}&background=0D0D12&color=fff`} 
                    className="w-full h-full object-cover" 
                    alt={user.username} 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-xs sm:text-sm text-white group-hover:text-purple-300 transition-colors truncate">
                    @{user.username}
                  </h4>
                  <p className="text-[10px] text-slate-400 font-medium truncate mt-0.5">
                    {user.fullname || 'Membro do Cardume'}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-[9px] font-bold text-purple-400 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.2 rounded">
                      {user.followers_count || 0} fãs
                    </span>
                  </div>
                </div>
                <i className="fas fa-chevron-right text-[10px] text-slate-600 group-hover:text-purple-400 transition-colors shrink-0"></i>
              </Link>
            ))}
          </div>

          {/* Symmetrical, Mobile-Friendly Pagination Controls */}
          {totalMembers > membersPerPage && (
            <div className="flex items-center justify-center gap-2.5 sm:gap-4 pt-4 sm:pt-6">
              <button 
                type="button"
                disabled={membersPage === 1}
                onClick={() => setMembersPage(prev => Math.max(1, prev - 1))}
                className="w-10 h-10 sm:w-11 sm:h-11 bg-slate-900 border border-slate-800 rounded-xl text-white disabled:opacity-30 hover:bg-slate-800 transition-colors flex items-center justify-center cursor-pointer"
                title="Página Anterior"
              >
                <i className="fas fa-chevron-left text-xs"></i>
              </button>

              <div className="px-4 py-2 sm:px-6 sm:py-2.5 bg-slate-900/80 border border-slate-800 rounded-xl text-xs font-bold text-white tracking-wide">
                <span>Página {membersPage}</span>
                <span className="text-slate-500 mx-1.5">/</span>
                <span className="text-slate-400">{Math.ceil(totalMembers / membersPerPage)}</span>
              </div>

              <button 
                type="button"
                disabled={membersPage * membersPerPage >= totalMembers}
                onClick={() => setMembersPage(prev => prev + 1)}
                className="w-10 h-10 sm:w-11 sm:h-11 bg-slate-900 border border-slate-800 rounded-xl text-white disabled:opacity-30 hover:bg-slate-800 transition-colors flex items-center justify-center cursor-pointer"
                title="Próxima Página"
              >
                <i className="fas fa-chevron-right text-xs"></i>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Social;
